import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, Check, Crown, Rocket, Star, Lock, AlertCircle, Layers, Tag, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import LegalFooter from '../components/LegalFooter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Pricing() {
  const [tiers, setTiers] = useState({});
  const [allTiers, setAllTiers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoValidated, setPromoValidated] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchTiers();
  }, []);

  const fetchTiers = async () => {
    try {
      const [tiersRes, allTiersRes] = await Promise.all([
        axios.get(`${API_URL}/api/memberships/tiers`),
        axios.get(`${API_URL}/api/memberships/all-tiers`)
      ]);
      setTiers(tiersRes.data);
      setAllTiers(allTiersRes.data);
    } catch (error) {
      toast.error('Failed to load membership tiers');
    }
  };

  const handleSelectTier = async (tierId, isCustom = false) => {
    if (!user) {
      navigate('/register');
      return;
    }

    if (user.membership_tier === tierId) {
      toast.info('You already have this membership');
      return;
    }

    // Check if Founder tier is available
    if (tierId === 'founder' && tiers.founder) {
      if (!tiers.founder.is_open) {
        toast.error('Founder membership is currently closed');
        return;
      }
      if (tiers.founder.seats_remaining <= 0) {
        toast.error('All Founder seats have been filled');
        return;
      }
    }

    setLoading(true);
    setSelectedTier(tierId);
    try {
      const originUrl = window.location.origin;
      // Built-in tiers go through the recurring subscription flow.
      // Custom admin-created tiers still use the legacy one-time endpoint
      // until they're wired into the Stripe Subscription product catalog.
      const isBuiltIn = ['founder', 'growth', 'ascend'].includes(tierId);
      const endpoint = (isBuiltIn && !isCustom)
        ? `${API_URL}/api/subscriptions/checkout`
        : `${API_URL}/api/memberships/upgrade`;
      const payload = isBuiltIn && !isCustom
        ? { tier: tierId, origin_url: originUrl }
        : { tier: tierId, origin_url: originUrl, is_custom_tier: isCustom };
      if (!isBuiltIn && promoValidated) {
        payload.promo_code = promoValidated.code;
      }
      const response = await axios.post(endpoint, payload, { headers: getAuthHeader() });

      // Subscription endpoint returns { url, checkout_url, session_id }; legacy returns { checkout_url }.
      const redirect = response.data.checkout_url || response.data.url;
      window.location.href = redirect;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process upgrade');
      setLoading(false);
      setSelectedTier(null);
    }
  };

  const getTierIcon = (tierKey) => {
    switch (tierKey) {
      case 'founder':
        return <Crown className="w-8 h-8" />;
      case 'growth':
        return <Rocket className="w-8 h-8" />;
      case 'ascend':
        return <Star className="w-8 h-8" />;
      default:
        return <Layers className="w-8 h-8" />;
    }
  };

  const getTierGradient = (tierKey, tierLevel) => {
    switch (tierKey) {
      case 'founder':
        return 'from-orange-500 to-orange-600';
      case 'growth':
        return 'from-purple-500 to-indigo-600';
      case 'ascend':
        return 'from-blue-500 to-cyan-600';
      default:
        // Custom tiers get unique colors based on tier level
        if (tierLevel >= 4) return 'from-pink-500 to-rose-600';
        return 'from-emerald-500 to-teal-600';
    }
  };

  const tierOrder = ['ascend', 'growth', 'founder'];
  
  // Get custom tiers (not base tiers)
  const customTiers = allTiers.filter(t => 
    t.slug && !['ascend', 'growth', 'founder'].includes(t.slug)
  );

  const handleValidatePromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/promo-codes/validate`, { code: promoCode });
      setPromoValidated(res.data);
      toast.success(`Promo code applied! ${res.data.discount_percent}% off`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid promo code');
      setPromoValidated(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const clearPromo = () => {
    setPromoCode('');
    setPromoValidated(null);
  };

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Navigation */}
        <div className="flex justify-between items-center mb-8">
          <Link to="/">
            <Button variant="ghost" className="text-foreground hover:text-primary" data-testid="back-home-btn">
              <ArrowLeft className="mr-2 w-4 h-4" /> Back to Home
            </Button>
          </Link>
          {!user && (
            <Link to="/login">
              <Button variant="outline" data-testid="login-btn">
                Already a member? Login
              </Button>
            </Link>
          )}
        </div>

        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl sm:text-5xl font-playfair font-bold text-foreground mb-4">
            Choose Your Path to Success
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Select the membership tier that aligns with your business goals
          </p>
        </div>

        {/* Promo Code */}
        <div className="max-w-md mx-auto mb-12">
          <div className="wm-card p-4 rounded-lg">
            <label className="text-sm text-muted-foreground flex items-center gap-2 mb-2">
              <Tag className="w-4 h-4" /> Have a promo code?
            </label>
            {promoValidated ? (
              <div className="flex items-center justify-between bg-emerald-900/20 border border-emerald-500/30 rounded-lg p-3">
                <div>
                  <span className="text-emerald-400 font-bold">{promoValidated.code}</span>
                  <span className="text-muted-foreground ml-2">— {promoValidated.discount_percent}% off applied</span>
                </div>
                <button onClick={clearPromo} className="text-muted-foreground hover:text-foreground" data-testid="clear-promo-btn">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Input
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                  placeholder="Enter code"
                  className="bg-input text-foreground flex-1"
                  onKeyDown={(e) => e.key === 'Enter' && handleValidatePromo()}
                  data-testid="promo-code-input"
                />
                <Button
                  onClick={handleValidatePromo}
                  disabled={promoLoading || !promoCode.trim()}
                  variant="outline"
                  className="border-primary text-primary hover:bg-primary/10"
                  data-testid="apply-promo-btn"
                >
                  {promoLoading ? 'Checking...' : 'Apply'}
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Base Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-testid="pricing-cards-grid">
          {tierOrder.map((tierKey) => {
            const tier = tiers[tierKey];
            if (!tier) return null;

            const isCurrentTier = user?.membership_tier === tierKey;
            const isFounder = tierKey === 'founder';
            const isFounderLocked = isFounder && (!tier.is_open || tier.seats_remaining <= 0);
            const isPopular = tierKey === 'growth';

            return (
              <div
                key={tierKey}
                className={`relative wm-card rounded-2xl overflow-hidden transition-all duration-300 hover:scale-105 ${
                  isPopular ? 'ring-2 ring-primary' : ''
                } ${isFounderLocked ? 'opacity-75' : ''}`}
                data-testid={`pricing-card-${tierKey}`}
              >
                {/* Popular Badge */}
                {isPopular && (
                  <div className="absolute top-0 left-0 right-0 bg-primary text-primary-foreground text-center py-2 text-sm font-semibold">
                    Most Popular
                  </div>
                )}

                {/* Founder Seats Badge */}
                {isFounder && (
                  <div className={`absolute top-0 left-0 right-0 py-2 text-center text-sm font-semibold ${
                    isFounderLocked 
                      ? 'bg-red-500 text-white' 
                      : 'bg-gradient-to-r from-orange-500 to-orange-600 text-white'
                  }`}>
                    {isFounderLocked ? (
                      <span className="flex items-center justify-center gap-1">
                        <Lock className="w-4 h-4" /> Sold Out
                      </span>
                    ) : (
                      <span data-testid="founder-seats-remaining">
                        {tier.seats_remaining} Founder Seats Remaining
                      </span>
                    )}
                  </div>
                )}

                <div className={`p-8 ${isPopular || isFounder ? 'pt-14' : ''}`}>
                  {/* Tier Icon */}
                  <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${getTierGradient(tierKey)} flex items-center justify-center text-white mb-6`}>
                    {getTierIcon(tierKey)}
                  </div>

                  {/* Tier Name */}
                  <h3 className="text-2xl font-playfair font-bold text-foreground mb-2">
                    {tier.name}
                  </h3>

                  {/* Price */}
                  <div className="mb-6">
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-bold text-foreground">${tier.price}</span>
                      <span className="text-muted-foreground">/month</span>
                    </div>
                    {tier.initiation_fee > 0 && (
                      <p className={`text-sm mt-1 font-medium ${isFounder ? 'text-orange-600' : 'text-purple-600'}`}>
                        + ${tier.initiation_fee} one-time initiation fee
                      </p>
                    )}
                    {/* Listings count is shown in the features list below
                        — don't duplicate it here. */}
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-8">
                    {tier.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <Check className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                          isFounder ? 'text-orange-600' : isPopular ? 'text-primary' : 'text-green-600'
                        }`} />
                        <span className="text-muted-foreground text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <Button
                    onClick={() => handleSelectTier(tierKey)}
                    disabled={loading || isCurrentTier || isFounderLocked}
                    className={`w-full py-6 text-lg font-semibold ${
                      isFounder 
                        ? 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white' 
                        : isPopular 
                          ? 'bg-primary hover:bg-primary/90' 
                          : 'bg-secondary hover:bg-secondary/90'
                    }`}
                    data-testid={`select-tier-${tierKey}-btn`}
                  >
                    {loading && selectedTier === tierKey ? (
                      'Processing...'
                    ) : isCurrentTier ? (
                      'Current Plan'
                    ) : isFounderLocked ? (
                      'Unavailable'
                    ) : (
                      `Get ${tier.name.split(' ')[0]}`
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Custom Tiers Section */}
        {customTiers.length > 0 && (
          <div className="mt-16">
            <h2 className="text-2xl font-playfair font-bold text-foreground text-center mb-8">
              Special Membership Tiers
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {customTiers.map((tier) => {
                const isCurrentTier = user?.membership_tier === tier.slug;
                
                return (
                  <div
                    key={tier.id}
                    className="relative wm-card rounded-2xl overflow-hidden transition-all duration-300 hover:scale-105 border-2 border-pink-500/30"
                    data-testid={`pricing-card-${tier.slug}`}
                  >
                    <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-pink-500 to-rose-600 text-white text-center py-2 text-sm font-semibold">
                      Exclusive Tier
                    </div>

                    <div className="p-8 pt-14">
                      {/* Tier Icon */}
                      <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${getTierGradient(tier.slug, tier.tier_level)} flex items-center justify-center text-white mb-6`}>
                        <Layers className="w-8 h-8" />
                      </div>

                      {/* Tier Name */}
                      <h3 className="text-2xl font-playfair font-bold text-foreground mb-2">
                        {tier.name}
                      </h3>

                      {/* Price */}
                      <div className="mb-6">
                        <div className="flex items-baseline gap-1">
                          <span className="text-4xl font-bold text-foreground">${tier.price}</span>
                          <span className="text-muted-foreground">/month</span>
                        </div>
                        {tier.initiation_fee > 0 && (
                          <p className="text-sm mt-1 font-medium text-pink-400">
                            + ${tier.initiation_fee} one-time initiation fee
                          </p>
                        )}
                        {tier.listings_allocation > 0 && (
                          <p className="text-sm text-green-400 mt-1 font-medium">
                            {tier.listings_allocation.toLocaleString()} Directory Listings
                          </p>
                        )}
                      </div>

                      {/* Features */}
                      <ul className="space-y-3 mb-8">
                        {tier.features?.map((feature, index) => (
                          <li key={index} className="flex items-start gap-3">
                            <Check className="w-5 h-5 mt-0.5 flex-shrink-0 text-pink-400" />
                            <span className="text-muted-foreground text-sm">{feature}</span>
                          </li>
                        ))}
                      </ul>

                      {/* CTA Button */}
                      <Button
                        onClick={() => handleSelectTier(tier.slug, true)}
                        disabled={loading || isCurrentTier}
                        className="w-full py-6 text-lg font-semibold bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-600 hover:to-rose-700 text-white"
                        data-testid={`select-tier-${tier.slug}-btn`}
                      >
                        {loading && selectedTier === tier.slug ? (
                          'Processing...'
                        ) : isCurrentTier ? (
                          'Current Plan'
                        ) : (
                          `Get ${tier.name}`
                        )}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Feature Comparison Note */}
        <div className="mt-16 max-w-4xl mx-auto">
          <div className="wm-card p-8 rounded-xl">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Important Information</h3>
                <div className="text-sm text-muted-foreground space-y-2">
                  <p>• Membership fees provide access to education, community, and platform tools.</p>
                  <p>• Only Founder members are eligible for the $1 Million leaderboard and award.</p>
                  <p>• Founder membership includes a one-time $500 initiation fee charged with first payment.</p>
                  <p>• Recognition and rewards are based solely on verified sales performance.</p>
                  <p>• We do not guarantee income or financial results. This is not an investment vehicle.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* We Masterminds Branding */}
        <div className="mt-12 text-center">
          <Link to="/" className="inline-flex items-center gap-2 text-primary hover:text-primary/80 transition-colors">
            <span className="text-lg font-playfair font-bold">We Masterminds</span>
          </Link>
          <p className="text-sm text-muted-foreground mt-2">Performance-Based Business Excellence</p>
        </div>
      </div>
      <LegalFooter />
    </div>
  );
}
